// =============================================================================
// Module Project - Repository: Collaboration (Members & Assignees)
// Migrated to new permission model (Phase 6B)
// =============================================================================

import { supabase } from '@/integrations/supabase/client';
import type { PermissionLevel } from './permissionRepository';

// Re-export from permissionRepository for backward compatibility
export type { PermissionLevel } from './permissionRepository';

export interface ProjectMemberInfo {
  org_member_id: string;
  effective_permission: PermissionLevel;
  source: 'project' | 'space';
  user_id: string;
  full_name: string | null;
  email: string;
  avatar_url: string | null;
}

export interface TaskAssigneeInfo {
  task_id: string;
  org_member_id: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// ORGANIZATION MEMBER INFO (for dialogs)
// ─────────────────────────────────────────────────────────────────────────────
export interface OrgMemberInfo {
  id: string;
  user_id: string;
  full_name: string | null;
  email: string;
  avatar_url: string | null;
}

// ─────────────────────────────────────────────────────────────────────────────
// FETCH ORGANIZATION MEMBERS (for adding to project)
// ─────────────────────────────────────────────────────────────────────────────
export async function fetchOrganizationMembers(orgId: string): Promise<OrgMemberInfo[]> {
  const { data: membersData, error } = await supabase
    .from('core_organization_members')
    .select('id, user_id')
    .eq('org_id', orgId)
    .eq('status', 'active');

  if (error) throw error;
  if (!membersData || membersData.length === 0) return [];

  const userIds = membersData.map((m) => m.user_id);
  const { data: profilesData } = await supabase
    .from('core_profiles')
    .select('id, full_name, email, avatar_url')
    .in('id', userIds);

  const profileMap = new Map((profilesData || []).map((p) => [p.id, p]));

  return membersData.map((m) => {
    const profile = profileMap.get(m.user_id);
    return {
      id: m.id,
      user_id: m.user_id,
      full_name: profile?.full_name || null,
      email: profile?.email || '',
      avatar_url: profile?.avatar_url || null,
    };
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// FETCH PROJECT MEMBERS (NEW: using RPC get_project_members)
// Returns members with explicit access (direct or space-inherited)
// Owner/Admin have implicit access but are NOT listed
// ─────────────────────────────────────────────────────────────────────────────
export async function fetchProjectMembers(
  projectId: string,
  _orgId?: string // Kept for backward compatibility, not used
): Promise<ProjectMemberInfo[]> {
  const { data, error } = await supabase.rpc('get_project_members', {
    _project_id: projectId,
  });

  if (error) {
    console.error('Error fetching project members:', error);
    throw error;
  }

  return (data || []).map((m: any) => ({
    org_member_id: m.org_member_id,
    user_id: m.user_id,
    effective_permission: m.effective_permission as PermissionLevel,
    source: m.source as 'project' | 'space',
    full_name: m.full_name,
    email: m.email,
    avatar_url: m.avatar_url,
  }));
}

// ─────────────────────────────────────────────────────────────────────────────
// FETCH TASK ASSIGNEES
// ─────────────────────────────────────────────────────────────────────────────
export async function fetchTaskAssignees(projectId: string): Promise<TaskAssigneeInfo[]> {
  // First get all task IDs for this project
  const { data: tasksData } = await supabase
    .from('project_tasks')
    .select('id')
    .eq('project_id', projectId);

  if (!tasksData || tasksData.length === 0) return [];

  const taskIds = tasksData.map((t) => t.id);

  const { data: assigneesData, error } = await supabase
    .from('project_task_assignees')
    .select('task_id, org_member_id')
    .in('task_id', taskIds);

  if (error) throw error;
  return (assigneesData || []) as TaskAssigneeInfo[];
}

// ─────────────────────────────────────────────────────────────────────────────
// ASSIGN TASK (via Edge Function)
// ─────────────────────────────────────────────────────────────────────────────
export async function assignTask(
  orgId: string,
  taskId: string,
  orgMemberIds: string[]
): Promise<void> {
  const { error } = await supabase.functions.invoke('assign-task', {
    body: { org_id: orgId, task_id: taskId, org_member_ids: orgMemberIds },
  });

  if (error) throw error;
}

// ─────────────────────────────────────────────────────────────────────────────
// UNASSIGN TASK (via Edge Function)
// ─────────────────────────────────────────────────────────────────────────────
export async function unassignTask(
  orgId: string,
  taskId: string,
  orgMemberId: string
): Promise<void> {
  const { error } = await supabase.functions.invoke('unassign-task', {
    body: { org_id: orgId, task_id: taskId, org_member_id: orgMemberId },
  });

  if (error) throw error;
}

// ─────────────────────────────────────────────────────────────────────────────
// BATCH UPDATE ASSIGNEES (via Edge Function)
// Single API call for multiple add/remove operations
// ─────────────────────────────────────────────────────────────────────────────
export async function batchUpdateAssignees(
  orgId: string,
  taskId: string,
  addMemberIds: string[],
  removeMemberIds: string[]
): Promise<{ added: number; removed: number }> {
  const { data, error } = await supabase.functions.invoke('batch-update-assignees', {
    body: { 
      org_id: orgId, 
      task_id: taskId, 
      add_member_ids: addMemberIds,
      remove_member_ids: removeMemberIds
    },
  });

  if (error) throw error;
  return { added: data?.added || 0, removed: data?.removed || 0 };
}

// ─────────────────────────────────────────────────────────────────────────────
// ADD PROJECT PERMISSION (via RPC - replaces edge function)
// ─────────────────────────────────────────────────────────────────────────────
export async function addProjectPermission(
  projectId: string,
  orgMemberId: string,
  permission: PermissionLevel = 'view'
): Promise<void> {
  const { error } = await supabase.rpc('manage_project_permission_add', {
    _project_id: projectId,
    _org_member_id: orgMemberId,
    _permission: permission,
  });

  if (error) throw error;
}

// ─────────────────────────────────────────────────────────────────────────────
// UPDATE PROJECT PERMISSION (via RPC - replaces edge function)
// ─────────────────────────────────────────────────────────────────────────────
export async function updateProjectPermission(
  orgId: string,
  projectId: string,
  orgMemberId: string,
  permission: PermissionLevel
): Promise<void> {
  const { error } = await supabase.rpc('manage_project_permission_update', {
    _org_id: orgId,
    _project_id: projectId,
    _org_member_id: orgMemberId,
    _permission: permission,
  });

  if (error) throw error;
}

// ─────────────────────────────────────────────────────────────────────────────
// REMOVE PROJECT PERMISSION (via RPC - replaces edge function)
// ─────────────────────────────────────────────────────────────────────────────
export async function removeProjectPermission(
  orgId: string,
  projectId: string,
  orgMemberId: string
): Promise<void> {
  const { error } = await supabase.rpc('manage_project_permission_remove', {
    _org_id: orgId,
    _project_id: projectId,
    _org_member_id: orgMemberId,
  });

  if (error) throw error;
}

// =============================================================================
// LEGACY COMPATIBILITY ALIASES (deprecated - use new functions)
// =============================================================================

/** @deprecated Use addProjectPermission instead */
export const addProjectMember = async (
  projectId: string,
  orgMemberId: string,
  _role: 'project_lead' | 'contributor' | 'viewer' = 'viewer'
): Promise<void> => {
  // Map legacy roles to new permissions
  const permissionMap: Record<string, PermissionLevel> = {
    project_lead: 'full',
    contributor: 'edit',
    viewer: 'view',
  };
  await addProjectPermission(projectId, orgMemberId, permissionMap[_role] || 'view');
};

/** @deprecated Use updateProjectPermission instead */
export const updateProjectMemberRole = async (
  orgId: string,
  projectId: string,
  orgMemberId: string,
  _role: 'project_lead' | 'contributor' | 'viewer'
): Promise<void> => {
  const permissionMap: Record<string, PermissionLevel> = {
    project_lead: 'full',
    contributor: 'edit',
    viewer: 'view',
  };
  await updateProjectPermission(orgId, projectId, orgMemberId, permissionMap[_role] || 'view');
};

/** @deprecated Use removeProjectPermission instead */
export const removeProjectMember = async (
  orgId: string,
  projectId: string,
  orgMemberId: string
): Promise<void> => {
  await removeProjectPermission(orgId, projectId, orgMemberId);
};

/** @deprecated No longer applicable - ownership transferred via permission model */
export const transferProjectLead = async (
  _orgId: string,
  _projectId: string,
  _newLeadMemberId: string
): Promise<void> => {
  console.warn('transferProjectLead is deprecated. Use permission model instead.');
  // No-op - the concept of a single "project lead" no longer exists
  // Users with "full" permission can manage the project
};
